import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import { deleteDocument, getDocuments, uploadDocument } from "../api/documents";
import { getApiError } from "../api/errors";
import { EmptyPanel, LoadingPanel } from "../components/PageState";
import { useAuth } from "../context/AuthContext";
import MainLayout from "../layouts/MainLayout";
import { formatDate, formatFileSize, titleCase } from "../utils/format";

const MAX_FILE_SIZE = 20 * 1024 * 1024;

export default function Documents() {
  const { canManage, canEdit, selectedWorkspace } = useAuth();
  const inputRef = useRef(null);
  const [documents, setDocuments] = useState([]);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [deletingId, setDeletingId] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await getDocuments();
      setDocuments(Array.isArray(response.data) ? response.data : []);
    } catch (requestError) {
      setError(getApiError(requestError, "Could not load documents."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, selectedWorkspace?.public_id]);

  const chooseFile = (selected) => {
    setError("");
    if (!selected) return;
    if (!selected.name.toLowerCase().endsWith(".pdf")) {
      setError("Only PDF files are allowed.");
      return;
    }
    if (selected.size <= 0) {
      setError("The selected file is empty.");
      return;
    }
    if (selected.size > MAX_FILE_SIZE) {
      setError("The PDF exceeds the 20 MB upload limit.");
      return;
    }
    setFile(selected);
  };

  const upload = async () => {
    if (!file) {
      setError("Select a PDF first.");
      return;
    }

    setUploading(true);
    setProgress(0);
    setError("");
    setSuccess("");
    try {
      await uploadDocument(file, (event) => {
        if (event.total) setProgress(Math.round((event.loaded / event.total) * 100));
      });
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
      setSuccess("Document processed and added to the workspace knowledge base.");
      await load();
    } catch (requestError) {
      setError(getApiError(requestError, "Could not process the PDF."));
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const remove = async (document) => {
    if (!window.confirm(`Delete ${document.original_name}?`)) return;
    setDeletingId(document.public_id);
    setError("");
    try {
      await deleteDocument(document.public_id);
      setDocuments((current) => current.filter((item) => item.public_id !== document.public_id));
      setSuccess("Document deleted.");
    } catch (requestError) {
      setError(getApiError(requestError, "Could not delete the document."));
    } finally {
      setDeletingId("");
    }
  };

  return (
    <MainLayout>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={2} mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={900} letterSpacing={-0.7}>Knowledge documents</Typography>
          <Typography color="text.secondary" sx={{ mt: 0.75 }}>
            Workspace-isolated PDFs used by retrieval and knowledge agents.
          </Typography>
        </Box>
        <Chip label={`${documents.length} documents`} sx={{ alignSelf: { xs: "flex-start", md: "center" }, fontWeight: 800 }} />
      </Stack>

      {!canEdit && <Alert severity="info" sx={{ mb: 3, borderRadius: 3 }}>You have read-only document access.</Alert>}
      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }} onClose={() => setError("")}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3, borderRadius: 3 }} onClose={() => setSuccess("")}>{success}</Alert>}

      {canEdit && (
        <Card sx={{ mb: 3.5, borderRadius: 4, border: "1px solid #e2e8f0", boxShadow: "0 14px 35px rgba(15,23,42,.05)" }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={900}>Upload PDF</Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5, mb: 2.5 }}>
              PDF only, up to 20 MB. Uploading performs extraction, chunking, and embeddings.
            </Typography>
            <Box sx={{ p: 2.5, borderRadius: 4, border: "1px dashed #94a3b8", bgcolor: "#f8fafc" }}>
              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} gap={2}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography fontWeight={900}>{file ? file.name : "No PDF selected"}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {file ? formatFileSize(file.size) : "Choose a warranty, return, policy, or operational document."}
                  </Typography>
                </Box>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <Button component="label" variant="outlined" disabled={uploading} sx={{ textTransform: "none", fontWeight: 800 }}>
                    Choose PDF
                    <input ref={inputRef} hidden type="file" accept="application/pdf,.pdf" onChange={(event) => chooseFile(event.target.files?.[0])} />
                  </Button>
                  <Button variant="contained" disabled={!file || uploading} onClick={upload} sx={{ textTransform: "none", fontWeight: 800 }}>
                    {uploading ? <CircularProgress size={21} color="inherit" /> : "Upload and process"}
                  </Button>
                </Stack>
              </Stack>
              {uploading && <LinearProgress variant={progress ? "determinate" : "indeterminate"} value={progress} sx={{ mt: 2, borderRadius: 5 }} />}
            </Box>
          </CardContent>
        </Card>
      )}

      {loading ? <LoadingPanel label="Loading documents…" /> : documents.length === 0 ? (
        <EmptyPanel icon="📄" title="No knowledge documents" description="Upload a PDF to ground policy, warranty, and operational answers." />
      ) : (
        <Grid container spacing={2.5}>
          {documents.map((document) => (
            <Grid item xs={12} sm={6} lg={4} key={document.public_id}>
              <Card sx={{ height: "100%", borderRadius: 4, border: "1px solid #e2e8f0", boxShadow: "0 14px 35px rgba(15,23,42,.05)" }}>
                <CardContent sx={{ p: 2.75 }}>
                  <Stack direction="row" spacing={1.4} alignItems="flex-start">
                    <Box sx={{ width: 46, height: 46, borderRadius: 3, display: "grid", placeItems: "center", bgcolor: "#ecfeff", fontSize: 24, flexShrink: 0 }}>📄</Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="h6" fontWeight={900} sx={{ overflowWrap: "anywhere" }}>{document.original_name}</Typography>
                      <Typography variant="body2" color="text.secondary">{formatFileSize(document.file_size)}</Typography>
                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
                    <Chip label={titleCase(document.processing_status)} size="small" color={document.processing_status === "completed" ? "success" : "default"} />
                    <Chip label={document.mime_type} size="small" variant="outlined" />
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1.75 }}>Uploaded {formatDate(document.created_at)}</Typography>
                  {canManage && (
                    <Button variant="outlined" color="error" disabled={deletingId === document.public_id} onClick={() => remove(document)} sx={{ mt: 2.25, textTransform: "none", fontWeight: 800 }}>
                      {deletingId === document.public_id ? "Deleting…" : "Delete"}
                    </Button>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </MainLayout>
  );
}
